import React, { useState, useEffect, useRef } from 'react';

const API = `${process.env.REACT_APP_API_URL}`;
const AVATAR_COLORS = ['#ec4899', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#6366f1'];

function getColor(name) {
  let hash = 0;
  for (let c of (name || '')) hash += c.charCodeAt(0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

function initials(name) {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

function MobileProviderSearch({ user, onPatientFound }) {
  const [searchId, setSearchId] = useState('');
  const [patient, setPatient] = useState(null);
  const [searching, setSearching] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [requestStatus, setRequestStatus] = useState('none'); // 'none' | 'pending' | 'approved'
  const [showScanner, setShowScanner] = useState(false);
  const [scannerReady, setScannerReady] = useState(false);
  const [history, setHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`provider_search_${user?.id}`) || '[]'); }
    catch { return []; }
  });
  const html5QrRef = useRef(null);

  useEffect(() => {
    if (!window.Html5Qrcode) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/html5-qrcode@2.3.8/html5-qrcode.min.js';
      script.onload = () => setScannerReady(true);
      document.head.appendChild(script);
    } else setScannerReady(true);
  }, []);

  const saveHistory = (p) => {
    const pUid = p.patientUid || p.uid || p.id;
    const updated = [p, ...history.filter(h => (h.patientUid || h.uid || h.id) !== pUid)].slice(0, 10);
    setHistory(updated);
    localStorage.setItem(`provider_search_${user?.id}`, JSON.stringify(updated));
  };

  const clearHistory = () => {
    setHistory([]);
    localStorage.removeItem(`provider_search_${user?.id}`);
  };

  const checkStatus = async (p) => {
    const pUid = p.patientUid || p.uid || p.id;
    try {
      const res = await fetch(`${API}/api/ProviderAccess/status/${user.id}/${pUid}`);
      if (res.ok) {
        const data = await res.json();
        const status = (data.status || 'none').toLowerCase();
        if (status === 'approved' || status === 'accepted') {
          setRequestStatus('approved');
        } else if (status === 'pending') {
          setRequestStatus('pending');
        } else {
          setRequestStatus('none');
        }
      } else {
        setRequestStatus('none');
      }
    } catch {
      setRequestStatus('none');
    }
  };

  const handleSearch = async (uid) => {
    const id = uid || searchId.trim();
    if (!id) { setError('Please enter a Patient ID.'); return; }
    setSearching(true); setError(''); setPatient(null); setRequestStatus('none');
    
    try {
      const res = await fetch(`${API}/api/Auth/search-patient?uid=${id}`);
      const data = await res.json();
      
      if (res.ok && data) {
        // Normalize patient object keys
        const normalizedPatient = {
          ...data,
          patientUid: data.patientUid || data.uid || data.id || id,
          name: data.name || data.patientName || 'Patient'
        };
        
        setPatient(normalizedPatient);
        saveHistory(normalizedPatient);
        await checkStatus(normalizedPatient);
      } else {
        setError('No patient found with this ID.');
      }
    } catch { 
      setError('Cannot connect to server.'); 
    }
    setSearching(false);
  };

  const handleSendRequest = async () => {
    if (!patient) return;
    const pUid = patient.patientUid || patient.uid || patient.id;
    
    setSending(true); setError('');
    try {
      const res = await fetch(`${API}/api/ProviderAccess/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ providerId: user.id, patientUid: pUid })
      });
      const data = await res.json();
      if (res.ok) {
        if (data.alreadyApproved) setRequestStatus('approved');
        else setRequestStatus('pending');
      } else {
        setError(data.message || 'Failed to send request.');
      }
    } catch { 
      setError('Cannot connect to server.'); 
    }
    setSending(false);
  };

  const startScanner = async () => {
    if (!scannerReady) { alert('Scanner not ready.'); return; }
    setShowScanner(true);
    setTimeout(async () => {
      try {
        html5QrRef.current = new window.Html5Qrcode('provider-qr-reader');
        await html5QrRef.current.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          (text) => { stopScanner(); setSearchId(text); handleSearch(text); },
          () => {}
        );
      } catch { setShowScanner(false); setError('Camera access denied.'); }
    }, 300);
  };

  const stopScanner = async () => {
    try { if (html5QrRef.current) { await html5QrRef.current.stop(); html5QrRef.current = null; } } catch { }
    setShowScanner(false);
  };

  if (showScanner) return (
    <div className="fixed inset-0 z-[200] bg-black flex flex-col" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      <div className="flex items-center justify-between px-4 py-4">
        <button onClick={stopScanner} className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
          <span className="material-symbols-outlined text-white">close</span>
        </button>
        <p className="text-white font-bold">Scan Patient QR Code</p>
        <div className="w-10" />
      </div>
      <div className="flex-1 flex flex-col items-center justify-center px-4">
        <div id="provider-qr-reader" style={{ width: '100%', maxWidth: '320px' }} className="rounded-2xl overflow-hidden" />
        <p className="text-white/70 text-sm mt-6 text-center">Point camera at patient's QR code</p>
      </div>
    </div>
  );

  return (
    <div className="p-4 space-y-4" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>

      {/* Main Search Input Card */}
      <section className="bg-white p-5 rounded-3xl shadow-sm border border-slate-100 space-y-4">
        <div>
          <h2 className="font-extrabold text-lg text-gray-900">Patient Search</h2>
          <p className="text-xs font-medium text-gray-400 mt-0.5">Search by ID or scan QR code</p>
        </div>

        {/* Search Input Box */}
        <div className="flex gap-2">
          <div className="flex-1 flex items-center gap-2 border border-slate-200 rounded-2xl px-3.5 py-3 focus-within:border-purple-500 transition-colors">
            <span className="material-symbols-outlined text-gray-400" style={{ fontSize: '20px' }}>search</span>
            <input 
              type="text" 
              placeholder="Enter Patient ID (MHJ...)"
              value={searchId} 
              onChange={e => { setSearchId(e.target.value); setError(''); }}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              className="flex-1 bg-transparent outline-none text-sm text-gray-900 font-medium placeholder-gray-400" 
            />
          </div>
          <button 
            onClick={() => handleSearch()} 
            disabled={searching}
            className="px-5 py-3 rounded-2xl text-white font-bold text-sm bg-blue-600 hover:bg-blue-700 active:scale-95 disabled:opacity-50 transition-all shadow-sm"
          >
            {searching ? 'Searching...' : 'Search'}
          </button>
        </div>
        
        {error && <p className="text-rose-500 text-xs font-semibold px-1">{error}</p>}

        {/* Separator */}
        <div className="flex items-center gap-3 py-1">
          <div className="flex-1 h-px bg-slate-100" />
          <span className="text-[11px] font-extrabold text-gray-400 uppercase tracking-wider">OR</span>
          <div className="flex-1 h-px bg-slate-100" />
        </div>

        {/* QR Scanner Area */}
        <div className="border-2 border-dashed border-blue-200 bg-blue-50/30 rounded-2xl p-6 flex flex-col items-center justify-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-blue-100/70 flex items-center justify-center text-blue-600">
            <span className="material-symbols-outlined text-2xl">qr_code_scanner</span>
          </div>
          <div className="text-center">
            <p className="font-extrabold text-sm text-gray-900">Scan Patient QR Code</p>
            <p className="text-xs text-gray-400 mt-0.5">Point camera at patient's MHJ QR code</p>
          </div>
          <button 
            onClick={startScanner}
            className="flex items-center gap-2 text-white font-bold px-6 py-2.5 rounded-full text-xs active:scale-95 transition-all shadow-sm mt-1"
            style={{ background: 'linear-gradient(90deg, #2563eb 0%, #7c3aed 100%)' }}
          >
            <span className="material-symbols-outlined text-base">photo_camera</span>
            Open Camera
          </button>
        </div>
      </section>

      {/* ── PATIENT FOUND & ACCESS REQUEST CARD ── */}
      {patient && (
        <section className="bg-white rounded-3xl border-2 border-purple-200 p-5 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-extrabold uppercase tracking-wider text-purple-600 bg-purple-50 px-3 py-1 rounded-full">
              Patient Found
            </span>
            <button 
              onClick={() => { setPatient(null); setSearchId(''); setError(''); }} 
              className="text-gray-400 hover:text-gray-600 text-xs font-bold"
            >
              Clear
            </button>
          </div>

          <div className="flex items-center gap-3 pt-1">
            <div 
              className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-black shrink-0 text-lg shadow-sm"
              style={{ background: getColor(patient.name || patient.patientName) }}
            >
              {initials(patient.name || patient.patientName)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-extrabold text-lg text-gray-900 truncate">{patient.name || patient.patientName}</p>
              <p className="text-xs font-mono font-bold text-purple-600 mt-0.5">{patient.patientUid || patient.uid}</p>
              <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 mt-1">
                <span className="material-symbols-outlined text-xs">check_circle</span> Verified Patient
              </span>
            </div>
          </div>

          {/* STATE 1: NO REQUEST YET -> SHOW SEND REQUEST BUTTON */}
          {requestStatus === 'none' && (
            <div className="space-y-3 pt-2">
              <div className="bg-amber-50 border border-amber-200/60 rounded-2xl p-3.5 flex gap-2.5 items-start">
                <span className="material-symbols-outlined text-amber-500 shrink-0 text-lg mt-0.5" style={{ fontVariationSettings: '"FILL" 1' }}>info</span>
                <p className="text-xs text-amber-800 font-medium leading-relaxed">
                  You need access permission to upload medical reports for <strong>{patient.name || patient.patientName}</strong>. Send an access request below.
                </p>
              </div>
              <button 
                onClick={handleSendRequest} 
                disabled={sending}
                className="w-full py-4 rounded-2xl text-white font-bold text-sm flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50 transition-all shadow-md"
                style={{ background: 'linear-gradient(90deg, #2563eb 0%, #7c3aed 100%)' }}
              >
                <span className="material-symbols-outlined text-lg" style={{ fontVariationSettings: '"FILL" 1' }}>send</span>
                {sending ? 'Sending Request...' : 'Send Access Request'}
              </button>
            </div>
          )}

          {/* STATE 2: REQUEST IS PENDING */}
          {requestStatus === 'pending' && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 text-center mt-2">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 mx-auto mb-2">
                <span className="material-symbols-outlined text-xl" style={{ fontVariationSettings: '"FILL" 1' }}>schedule</span>
              </div>
              <p className="font-extrabold text-amber-800 text-sm">Request Sent!</p>
              <p className="text-xs text-amber-700 font-medium mt-1">
                Waiting for <strong>{patient.name || patient.patientName}</strong> to approve your upload access request.
              </p>
            </div>
          )}

          {/* STATE 3: REQUEST IS APPROVED */}
          {requestStatus === 'approved' && (
            <div className="space-y-3 pt-2">
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-3.5 flex items-center gap-2.5">
                <span className="material-symbols-outlined text-emerald-600 text-xl" style={{ fontVariationSettings: '"FILL" 1' }}>check_circle</span>
                <p className="text-xs font-bold text-emerald-800">Access Approved! You can now upload reports.</p>
              </div>
              <button 
                onClick={() => onPatientFound && onPatientFound(patient)}
                className="w-full py-4 rounded-2xl text-white font-bold text-sm flex items-center justify-center gap-2 active:scale-95 transition-all shadow-md"
                style={{ background: 'linear-gradient(90deg, #2563eb 0%, #7c3aed 100%)' }}
              >
                <span className="material-symbols-outlined text-lg" style={{ fontVariationSettings: '"FILL" 1' }}>upload_file</span>
                Proceed to Upload Report
              </button>
            </div>
          )}
        </section>
      )}

      {/* Recent Searches Section */}
      {history.length > 0 && (
        <section className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="flex justify-between items-center px-5 pt-4 pb-3">
            <h3 className="font-extrabold text-base text-gray-900">Recent Searches</h3>
            <button 
              onClick={clearHistory} 
              className="text-xs font-bold text-blue-600 hover:underline"
            >
              Clear All
            </button>
          </div>
          <div className="divide-y divide-slate-100">
            {history.map((h, i) => {
              const pName = h.name || h.patientName || 'Patient';
              const pUid = h.patientUid || h.uid || h.id;
              return (
                <button 
                  key={i} 
                  onClick={() => { setSearchId(pUid); handleSearch(pUid); }}
                  className="w-full flex items-center gap-3 px-5 py-3 hover:bg-slate-50 text-left transition-colors active:scale-[0.99]"
                >
                  <div 
                    className="w-10 h-10 rounded-2xl flex items-center justify-center text-white font-black text-xs shrink-0 shadow-sm"
                    style={{ background: getColor(pName) }}
                  >
                    {initials(pName)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm text-gray-900 truncate">{pName}</p>
                    <p className="text-xs text-gray-400 font-mono font-medium">{pUid}</p>
                  </div>
                  <span className="material-symbols-outlined text-gray-300 text-xl shrink-0">chevron_right</span>
                </button>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}

export default MobileProviderSearch;